import { useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import SearchField from '../common/SearchField';
import { useGetDeadStockPageMutation } from '../../api/reports/ReportApi';
import { DeadStockRow } from '../../types/reports';
import { exportToExcel } from '../../utils/exportExcel';
import { longDate } from '../../utils/dates';

const money = (value?: number | null) => Number(value || 0).toFixed(2);
const centred = { flex: 1, align: 'center', headerAlign: 'center' } as const;

/**
 * Stock that has not moved - money standing on a shelf.
 *
 * <p>Counted per batch from the day that consignment arrived, not per product: a
 * model that sells steadily can still have one bad lot bought at the wrong price,
 * and that lot is what the shop needs to see and discount.
 */
const DeadStockReport = () => {
  const [searchValue, setSearchValue] = useState('');
  const [daysInput, setDaysInput] = useState('90');
  const [rows, setRows] = useState<DeadStockRow[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [getDeadStockPage] = useGetDeadStockPageMutation();

  useEffect(() => {
    if (timeout.current) clearTimeout(timeout.current);
    setLoading(true);
    timeout.current = setTimeout(async () => {
      try {
        const response = await getDeadStockPage({
          searchValue,
          days: Number(daysInput) || 0,
          page: paginationModel.page,
          size: paginationModel.pageSize,
        }).unwrap();
        setRows(response?.data || []);
        setRowCount(response?.totalCount || 0);
      } catch {
        setRows([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, daysInput, paginationModel.page, paginationModel.pageSize]);

  /**
   * Any change of filter puts the list back on the first page.
   *
   * <p>Page 4 of the old results is not page 4 of the new ones - usually it is past
   * the end of them, and the shop is shown an empty grid for a search that matched.
   */
  useEffect(() => {
    setPaginationModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, daysInput]);

  const columns: GridColDef[] = [
    {
      field: 'batchNo',
      headerName: 'Batch',
      ...centred,
      minWidth: 130,
      renderCell: (row) => <Chip size="small" variant="outlined" label={String(row.value)} />,
    },
    { field: 'productName', headerName: 'Product', ...centred, minWidth: 220, align: 'left', headerAlign: 'left' },
    { field: 'brandName', headerName: 'Brand', ...centred, minWidth: 120 },
    {
      field: 'receivedDate',
      headerName: 'Received',
      ...centred,
      minWidth: 130,
      renderCell: (row) => longDate(row.value as string),
    },
    {
      field: 'ageDays',
      headerName: 'Dead Stock Days',
      ...centred,
      minWidth: 90,
      renderCell: (row) => (
        <Typography variant="body2" fontWeight={700} color={Number(row.value) > 180 ? 'error.main' : 'warning.main'}>
          {row.value ?? '-'} d
        </Typography>
      ),
    },
    { field: 'quantityRemaining', headerName: 'Left', ...centred, minWidth: 80 },
    {
      field: 'purchasePrice',
      headerName: 'Cost',
      ...centred,
      minWidth: 100,
      renderCell: (row) => money(row.value as number),
    },
    {
      field: 'stuckValue',
      headerName: 'Money stuck',
      ...centred,
      minWidth: 130,
      renderCell: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {money(row.value as number)}
        </Typography>
      ),
    },
  ];

  const stuckOnPage = rows.reduce((sum, row) => sum + Number(row.stuckValue || 0), 0);

  const download = () =>
    exportToExcel(
      rows.map((row) => ({
        Batch: row.batchNo,
        Product: row.productName,
        Brand: row.brandName ?? '',
        Received: row.receivedDate ?? '',
        'Dead Stock Days': row.ageDays ?? '',
        Remaining: row.quantityRemaining,
        Cost: row.purchasePrice ?? '',
        'Money stuck': row.stuckValue,
      })),
      `dead-stock-over-${daysInput}-days`,
    );

  return (
    <Box>
      <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center" sx={{ mb: 2 }}>
        <SearchField value={searchValue} onChange={setSearchValue} placeholder="Any words: product, brand, batch no, colour, RAM, storage..." width={300} />
        <TextField
          label="Unsold for more than"
          size="small"
          type="number"
          sx={{ width: 180 }}
          value={daysInput}
          onChange={(event) => setDaysInput(event.target.value)}
        />
        <Chip variant="outlined" color="warning" label={`${rowCount} batch(es)`} />
        <Chip color="error" label={`₹ ${money(stuckOnPage)} stuck on this page`} />
        <Box flexGrow={1} />
        <Button size="small" startIcon={<DownloadIcon />} onClick={download} disabled={rows.length === 0}>
          Excel
        </Button>
      </Stack>

      <DataGrid
        columns={columns}
        rows={rows}
        getRowId={(row) => row.batchId}
        rowCount={rowCount}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={(model: GridPaginationModel) => setPaginationModel(model)}
        pageSizeOptions={[10, 25, 50, 100]}
        disableRowSelectionOnClick
        loading={loading}
        sx={{ height: '65vh' }}
      />

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
        Oldest first. "Money stuck" is what the shop paid for what is left of that consignment.
      </Typography>
    </Box>
  );
};

export default DeadStockReport;
